import React, { useState, useEffect, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api from "@/lib/api";
import { toast } from "sonner";
import { ThemeToggle, LangToggle } from "@/components/Toggles";
import {
  LayoutDashboard, Users, ListOrdered, Monitor, Settings, ShieldCheck,
  Droplets, LogOut, Menu, X, ShoppingBag, BarChart3, CreditCard, Bell, AlertTriangle,
} from "lucide-react";

function AlertBell() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState({ count: 0, batches: [] });
  const [open, setOpen] = useState(false);
  const seenRef = useRef(null);
  useEffect(() => {
    let m = true;
    const load = () => api.get("/alerts/overdue").then((r) => {
      if (!m) return;
      const ids = new Set((r.data.batches || []).map((b) => b.id));
      if (seenRef.current) {
        const fresh = (r.data.batches || []).filter((b) => !seenRef.current.has(b.id));
        fresh.forEach((b) => toast.warning(`${b.client_name} · ${b.order_code}`, { description: `${t("overdue_toast")} — ${b.stage_label} (${b.hours}h)` }));
      }
      seenRef.current = ids;
      setData(r.data);
    }).catch(() => {});
    load();
    const i = setInterval(load, 60000);
    return () => { m = false; clearInterval(i); };
  }, [t]);
  return (
    <div className="relative">
      <button data-testid="alert-bell" onClick={() => setOpen((o) => !o)} className="relative flex h-9 w-9 items-center justify-center rounded-lg hover:bg-secondary">
        <Bell className="h-5 w-5" />
        {data.count > 0 && (
          <span data-testid="alert-bell-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">{data.count}</span>
        )}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div data-testid="alert-panel" className="absolute right-0 z-40 mt-2 w-80 rounded-xl border border-border bg-card p-3 shadow-lg animate-fade-in">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="h-4 w-4 text-red-600" /> {t("overdue_batches")}</p>
            {data.count === 0 ? (
              <p className="py-5 text-center text-xs text-muted-foreground">{t("no_overdue")}</p>
            ) : (
              <div className="max-h-72 space-y-1.5 overflow-y-auto">
                {data.batches.map((b) => (
                  <button key={b.id} data-testid={`alert-item-${b.order_code}`} onClick={() => { setOpen(false); navigate("/queue-management"); }}
                    className="flex w-full items-center justify-between gap-2 rounded-lg border border-border/60 px-3 py-2 text-left transition-colors hover:bg-secondary">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">{b.client_name}</span>
                      <span className="block truncate font-mono text-xs text-muted-foreground">{b.order_code} · {b.stage_label}</span>
                    </span>
                    <span className="shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">{b.hours}h</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const BASE_NAV = [
  { to: "/dashboard", key: "nav_dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/clients", key: "nav_clients", icon: Users, testid: "nav-clients" },
  { to: "/queue-management", key: "nav_queue", icon: ListOrdered, testid: "nav-queue" },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [...BASE_NAV];
  if (user?.modules?.analytics) nav.push({ to: "/analytics", key: "nav_analytics", icon: BarChart3, testid: "nav-analytics" });
  if (user?.modules?.store) nav.push({ to: "/products", key: "nav_store", icon: ShoppingBag, testid: "nav-store" });
  nav.push({ to: "/billing", key: "nav_billing", icon: CreditCard, testid: "nav-billing" });
  nav.push({ to: "/settings", key: "nav_settings", icon: Settings, testid: "nav-settings" });
  if (user?.role === "admin") nav.push({ to: "/admin", key: "nav_admin", icon: ShieldCheck, testid: "nav-admin" });

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const SidebarLinks = ({ onClick }) => (
    <nav className="flex flex-col gap-1">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={onClick}
          data-testid={item.testid}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`
          }
        >
          <item.icon className="h-[18px] w-[18px]" />
          {t(item.key)}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-border bg-card px-4 py-6 lg:flex">
        <div className="mb-8 flex items-center gap-2.5 px-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
            <Droplets className="h-5 w-5" />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight">e-olive Albania</span>
        </div>
        <SidebarLinks />
        <div className="mt-auto space-y-4">
          <div className="flex items-center gap-3 rounded-lg bg-secondary/60 px-3 py-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber text-sm font-bold text-olive">
              {(user?.name || "U").charAt(0)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{user?.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user?.mill_name}</p>
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="h-[18px] w-[18px]" /> {t("logout")}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-72 flex-col border-r border-border bg-card px-4 py-6 animate-fade-in">
            <div className="mb-8 flex items-center justify-between px-2">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
                  <Droplets className="h-5 w-5" />
                </div>
                <span className="font-heading text-xl font-bold">e-olive Albania</span>
              </div>
              <button onClick={() => setMobileOpen(false)} data-testid="close-drawer-btn"><X className="h-5 w-5" /></button>
            </div>
            <SidebarLinks onClick={() => setMobileOpen(false)} />
            <button
              onClick={handleLogout}
              className="mt-auto flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive"
            >
              <LogOut className="h-[18px] w-[18px]" /> {t("logout")}
            </button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex flex-1 flex-col lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-border bg-background/80 px-4 backdrop-blur-md sm:px-6 lg:px-8">
          <button className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="open-drawer-btn">
            <Menu className="h-6 w-6" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <AlertBell />
            <LangToggle />
            <ThemeToggle />
          </div>
        </header>
        <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
